import { useCallback } from 'react';
import { localize } from '@saviynt/common';
import { useIntl } from 'react-intl';

import {
  type SharedMessageKey,
  sharedMessages,
} from '../constants/messages/shared';

export type InvertedLocalize<M extends string> = (
  defaultMessage: M,
  placeholders?: object
) => string;

/**
 * A custom hook that provides an inverted localization function.
 *
 * Unlike traditional localization where you provide a message ID to get a localized string,
 * this hook allows you to provide the default message directly and it looks up the corresponding ID.
 * This is useful when you have a set of predefined messages and want to reference them by their content
 * rather than by IDs.
 *
 * @template M - The type of messages that can be localized
 * @param {Record<M, string>} invertedLookup - A mapping of default messages to their corresponding IDs
 * @returns {InvertedLocalize<M>} A function that takes a default message and optional placeholders
 *                               and returns the localized string
 *
 * @example
 * const l = useInvertedLocalize(componentMessages);
 *
 * // Use the default message directly (component-specific)
 * const greeting = l('Hello, {name}!', { name: 'User' });
 *
 * // Shared messages are automatically available
 * const submitText = l('Submit');
 *
 * @remarks
 * If the provided default message is not found in the invertedLookup,
 * it returns the default message as-is (without localization).
 */
const useInvertedLocalize = <M extends string>(invertedLookup?: {
  [K in M]: K extends SharedMessageKey ? never : string;
}): InvertedLocalize<M | SharedMessageKey> => {
  const intl = useIntl();

  return useCallback(
    (defaultMessage: M | SharedMessageKey, placeholders?: object): string => {
      // Check component-specific lookup first, then fall back to shared messages
      const id =
        invertedLookup?.[defaultMessage as M] ??
        sharedMessages[defaultMessage as SharedMessageKey];

      if (!id) {
        return `${defaultMessage}`;
      }

      return localize(
        intl,
        { id, defaultMessage: `${defaultMessage}` },
        placeholders
      );
    },
    [intl, invertedLookup]
  );
};

export default useInvertedLocalize;
